import React, { useEffect, useState } from "react";
import {
  fetchLiveStats, fetchDailyStats, fetchActivity, backfillFromReplays, modeLabel, MODE_LABELS,
  type LiveStats, type DailyRow, type ActivityRow,
} from "../../shared/admin-stats";
import styles from "./StatsPage.module.scss";

// Admin-only: who is playing right now, whether it is growing (this week against
// the last), and games, players, accounts and sign-ins per day. Online play only:
// local games never reach the server (Plausible counts those). The admins' own
// accounts are left out unless "Count admins" is on (an admin playing as a guest
// cannot be told apart).

const LIVE_EVERY_MS = 10_000;
const RANGES = [7, 30, 90];
const modeOrder = (m: string) => { const i = Object.keys(MODE_LABELS).indexOf(m); return i < 0 ? 99 : i; };

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  return s < 60 ? s + "s ago" : Math.round(s / 60) + " min ago";
}
function dayLabel(day: string): string {
  const d = new Date(day + "T12:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function Live() {
  const [live, setLive] = useState<LiveStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, tick] = useState(0);

  useEffect(() => {
    let on = true;
    const load = async () => {
      if (document.hidden) return;
      const res = await fetchLiveStats();
      if (!on) return;
      if ("error" in res) setError(res.error);
      else { setLive(res); setError(null); }
    };
    load();
    const timer = setInterval(() => { load(); tick((t) => t + 1); }, LIVE_EVERY_MS);
    return () => { on = false; clearInterval(timer); };
  }, []);

  if (!live) return <p className={styles.state}>{error ? `Could not load (${error}).` : "Loading…"}</p>;
  const modes = Object.entries(live.modes).sort((a, b) => modeOrder(a[0]) - modeOrder(b[0]));
  const waiting = Object.entries(live.waiting).sort((a, b) => modeOrder(a[0]) - modeOrder(b[0]));
  return (
    <>
      <div className={styles.now}>
        <div className={styles.big}>
          <b>{live.playing}</b>
          <span>in a match</span>
        </div>
        <div className={styles.big}>
          <b>{live.waitingTotal}</b>
          <span>waiting to start</span>
        </div>
        <div className={styles.big}>
          <b>{live.viewers}</b>
          <span>on the start page</span>
        </div>
        <div className={styles.updated}>{error ? `Refresh failed (${error}), ` : ""}updated {ago(live.at)}</div>
      </div>
      {modes.length > 0 && (
        <div className={styles.tiles}>
          {modes.map(([mode, s]) => (
            <div key={mode} className={styles.tile}>
              <div className={styles.tileName}>{modeLabel(mode)}</div>
              <div className={styles.tileNum}>{s.humans + s.guests}</div>
              <div className={styles.tileSub}>{s.humans} signed in, {s.guests} guests</div>
              <div className={styles.tileSub}>{s.matches} {s.matches === 1 ? "match" : "matches"}, {s.bots} bots</div>
            </div>
          ))}
        </div>
      )}
      {waiting.length > 0 && (
        <p className={styles.waiting}>
          Waiting: {waiting.map(([base, n]) => `${modeLabel(base)} ${n}`).join(", ")}
        </p>
      )}
    </>
  );
}

function Daily({ withAdmins, onAdmins }: { withAdmins: boolean; onAdmins: (included: boolean) => void }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<DailyRow[] | null>(null);
  const [state, setState] = useState<string>("loading");
  const [backfill, setBackfill] = useState<string | null>(null);
  const [activity, setActivity] = useState<Map<string, ActivityRow> | null>(null);
  const [activityState, setActivityState] = useState("loading");

  async function load(n = days) {
    setState("loading");
    const [res, act] = await Promise.all([fetchDailyStats(n, withAdmins), fetchActivity(n, withAdmins)]);
    if ("error" in res) return setState(res.error);
    if (res.unconfigured) return setState("unconfigured");
    setRows(res.rows);
    onAdmins(!!res.admins);
    setActivity("error" in act ? null : new Map(act.rows.map((r) => [r.day, r])));
    setActivityState("error" in act ? act.error : "ready");
    setState("ready");
  }
  useEffect(() => { load(days); }, [days, withAdmins]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runBackfill() {
    setBackfill("Filling in…");
    const res = await backfillFromReplays();
    setBackfill("error" in res ? `Failed (${res.error})` : `Added up to ${res.sent} matches${res.more ? " (more left: run again)" : ""}`);
    load();
  }

  const byDay = new Map<string, Record<string, DailyRow>>();
  for (const r of rows || []) {
    const d = byDay.get(r.day) || {};
    d[r.mode] = r;
    byDay.set(r.day, d);
  }
  const dayKeys = [...new Set([...byDay.keys(), ...(activity ? [...activity.keys()] : [])])].sort().reverse();
  const modes = [...new Set((rows || []).map((r) => r.mode).filter((m) => m !== "all"))].sort((a, b) => modeOrder(a) - modeOrder(b));
  const peak = Math.max(1, ...dayKeys.map((d) => byDay.get(d)!.all?.players || 0));

  return (
    <>
      <div className={styles.bar}>
        <div className={styles.ranges}>
          {RANGES.map((n) => (
            <button key={n} className={n === days ? styles.rangeOn : styles.range} onClick={() => setDays(n)}>{n} days</button>
          ))}
        </div>
        <button className={styles.btn} onClick={() => load()}>Refresh</button>
      </div>
      {state === "no_table" && <p className={styles.state}>The play log table is missing: run supabase migration 0016_play_log.sql.</p>}
      {state === "unconfigured" && <p className={styles.state}>This server has no Supabase configured.</p>}
      {state !== "ready" && state !== "loading" && state !== "no_table" && state !== "unconfigured" && <p className={styles.state}>Could not load ({state}).</p>}
      {state === "loading" && !rows && <p className={styles.state}>Loading…</p>}
      {activityState === "no_function" && <p className={styles.state}>Accounts, sign-ins and new players need supabase migration 0017_activity_stats.sql.</p>}
      {rows && dayKeys.length === 0 && state === "ready" && <p className={styles.state}>No games logged yet.</p>}
      {dayKeys.length > 0 && (
        <>
          <div className={styles.chart}>
            {[...dayKeys].reverse().map((d) => {
              const all = byDay.get(d)?.all;
              const n = all?.players || 0;
              return (
                <div key={d} className={styles.col} title={`${dayLabel(d)}: ${n} players, ${all?.games || 0} games`}>
                  <span className={styles.colNum}>{n}</span>
                  <div className={styles.colBar} style={{ height: `${(n / peak) * 100}%` }} />
                </div>
              );
            })}
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>All players</th>
                  {activity && <th>New players</th>}
                  {activity && <th>Accounts</th>}
                  {modes.map((m) => <th key={m}>{modeLabel(m)}</th>)}
                </tr>
              </thead>
              <tbody>
                {dayKeys.map((d) => {
                  const row = byDay.get(d) || {};
                  const all = row.all;
                  const a = activity?.get(d);
                  return (
                    <tr key={d}>
                      <td>{dayLabel(d)}</td>
                      <td>
                        <b>{all?.players || 0}</b>
                        <span className={styles.sub}>{all?.signed_in || 0} signed in, {all?.guests || 0} guests</span>
                      </td>
                      {activity && (
                        <td>
                          <b>{a?.new_players || 0}</b>
                          <span className={styles.sub}>{a?.returning_players || 0} returning</span>
                        </td>
                      )}
                      {activity && (
                        <td>
                          <b>{a?.new_accounts || 0}</b>
                          <span className={styles.sub}>new, {a?.logins || 0} signed in</span>
                        </td>
                      )}
                      {modes.map((m) => {
                        const r = row[m];
                        return (
                          <td key={m}>
                            {r ? (
                              <>
                                <b>{r.players}</b>
                                <span className={styles.sub}>{r.games} {m === "playnow" ? "rounds" : r.games === 1 ? "match" : "matches"}</span>
                              </>
                            ) : <span className={styles.none}>0</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className={styles.foot}>
        <button className={styles.btn} onClick={runBackfill} disabled={backfill === "Filling in…"}>Fill in from replays</button>
        {backfill && <span className={styles.sub}>{backfill}</span>}
      </div>
    </>
  );
}

// This week against the week before: per-day sums over the last 7 days and the
// 7 before them (today counts as a day, so early in the day the week reads low).
const TREND: { key: string; label: string; of: (d?: DailyRow, a?: ActivityRow) => number; avg?: boolean }[] = [
  { key: "players", label: "Players a day", of: (d) => d?.players || 0, avg: true },
  { key: "games", label: "Games", of: (d) => d?.games || 0 },
  { key: "new", label: "New players", of: (_d, a) => a?.new_players || 0 },
  { key: "back", label: "Returning a day", of: (_d, a) => a?.returning_players || 0, avg: true },
  { key: "accounts", label: "New accounts", of: (_d, a) => a?.new_accounts || 0 },
  { key: "logins", label: "Sign-ins a day", of: (_d, a) => a?.logins || 0, avg: true },
];

function isoDay(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Trend({ withAdmins }: { withAdmins: boolean }) {
  const [data, setData] = useState<{ daily: Map<string, DailyRow>; activity: Map<string, ActivityRow> | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let on = true;
    setData(null);
    Promise.all([fetchDailyStats(14, withAdmins), fetchActivity(14, withAdmins)]).then(([res, act]) => {
      if (!on) return;
      if ("error" in res) return setError(res.error);
      setData({
        daily: new Map(res.rows.filter((r) => r.mode === "all").map((r) => [r.day, r])),
        activity: "error" in act ? null : new Map(act.rows.map((r) => [r.day, r])),
      });
    });
    return () => { on = false; };
  }, [withAdmins]);

  if (!data) return <p className={styles.state}>{error ? `Could not load (${error}).` : "Loading…"}</p>;
  const sum = (from: number, of: (d?: DailyRow, a?: ActivityRow) => number) => {
    let n = 0;
    for (let i = from; i < from + 7; i++) { const day = isoDay(i); n += of(data.daily.get(day), data.activity?.get(day)); }
    return n;
  };
  return (
    <div className={styles.tiles}>
      {TREND.filter((m) => data.activity || !["new", "back", "accounts", "logins"].includes(m.key)).map((m) => {
        const now = sum(0, m.of), before = sum(7, m.of);
        const shown = (n: number) => (m.avg ? Math.round((n / 7) * 10) / 10 : n);
        const change = before > 0 ? Math.round(((now - before) / before) * 100) : null;
        return (
          <div key={m.key} className={styles.tile}>
            <div className={styles.tileName}>{m.label}</div>
            <div className={styles.tileNum}>
              {shown(now)}
              {change != null && change !== 0 && <span className={change > 0 ? styles.up : styles.down}>{change > 0 ? "+" : ""}{change}%</span>}
            </div>
            <div className={styles.tileSub}>{shown(before)} the week before</div>
          </div>
        );
      })}
    </div>
  );
}

export default function StatsPage() {
  const [withAdmins, setWithAdmins] = useState(false);
  const [adminsIn, setAdminsIn] = useState(false);
  return (
    <div className={styles.page}>
      <h2 className={styles.title}>Right now</h2>
      <Live />
      <div className={styles.titleRow}>
        <h2 className={styles.title}>Last 7 days</h2>
        <label className={styles.check}>
          <input type="checkbox" checked={withAdmins} onChange={(e) => setWithAdmins(e.target.checked)} />
          Count admins
        </label>
      </div>
      {adminsIn && !withAdmins && <p className={styles.state}>Admins are still counted: run supabase migration 0017_activity_stats.sql.</p>}
      <Trend withAdmins={withAdmins} />
      <h2 className={styles.title}>Per day</h2>
      <Daily withAdmins={withAdmins} onAdmins={setAdminsIn} />
    </div>
  );
}
